import { useState } from 'react';
import { LogIn, Mail, Menu, X } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { ROUTES } from '@/shared/config/routes';
import { SITE } from '@/shared/config/site';
import { cn } from '@/shared/lib/utils/cn';
import { Button, IconButton } from '@/shared/ui';
import { ThemeToggle } from '@/features/theme';
import { useAuth, useAuthModal } from '@/features/auth';
import { UserMenu } from './UserMenu';

const NAV = [
  { to: ROUTES.home, label: 'Articles', end: true },
  { to: ROUTES.about, label: 'About', end: false },
];

export interface SiteHeaderProps {
  /** Defaults to opening the global auth modal; override to customise. */
  onSignInClick?: () => void;
  /** Overridden by the subscribe modal in Milestone 8; defaults to a toast. */
  onSubscribeClick?: () => void;
}

export function SiteHeader({ onSignInClick, onSubscribeClick }: SiteHeaderProps) {
  const { user } = useAuth();
  const openAuth = useAuthModal((state) => state.open);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignIn = onSignInClick ?? (() => openAuth('login'));
  const handleSubscribe =
    onSubscribeClick ?? (() => toast.info('Newsletter sign-up arrives in a later milestone.'));

  return (
    <>
      <div className="fixed top-0 z-[70] h-[2px] w-full bg-amber-400" />
      <header className="sticky top-[2px] z-50 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-6 px-6">
          <Link
            to={ROUTES.home}
            className="shrink-0 font-display text-xl font-black italic tracking-tight transition-colors hover:text-amber-500"
          >
            {SITE.name}
          </Link>

          <nav className="ml-3 hidden gap-1 sm:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-full px-3.5 py-1.5 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:bg-zinc-100 hover:text-foreground dark:hover:bg-zinc-800/70',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <Button size="sm" onClick={handleSubscribe} className="hidden sm:inline-flex">
              <Mail className="h-3.5 w-3.5" />
              Subscribe
            </Button>

            {user ? (
              <UserMenu user={user} />
            ) : (
              <IconButton onClick={handleSignIn} aria-label="Sign in" title="Sign in">
                <LogIn className="h-4 w-4" />
              </IconButton>
            )}

            <IconButton
              className="text-foreground sm:hidden"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((open) => !open)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </IconButton>
          </div>
        </div>

        {mobileOpen ? (
          <div className="border-t border-border bg-background/95 backdrop-blur-xl sm:hidden">
            <div className="mx-auto flex max-w-5xl flex-col gap-1 px-6 py-3">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-xl px-3 py-2.5 text-sm font-medium text-foreground hover:bg-zinc-50 dark:hover:bg-zinc-800"
                >
                  {item.label}
                </NavLink>
              ))}
              {!user ? (
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    handleSignIn();
                  }}
                  className="rounded-xl px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-zinc-50 dark:hover:bg-zinc-800"
                >
                  Sign in
                </button>
              ) : null}
              <Button
                onClick={() => {
                  setMobileOpen(false);
                  handleSubscribe();
                }}
                className="mt-1.5 w-full"
              >
                <Mail className="h-3.5 w-3.5" />
                Subscribe free
              </Button>
            </div>
          </div>
        ) : null}
      </header>
    </>
  );
}
